/**
 * Auth PWA 2i Recouvrement : onboarding + login par code + session token.
 */

const API_BASE = '/api/public/verif';
const DEVICE_KEY = 'verif2i:device_id';
const SESSION_KEY = 'verif2i:session';
const AGENT_KEY = 'verif2i:agent';

// ---------------------------------------------------------------------------
// Device ID persistant (UUID v4 léger)
// ---------------------------------------------------------------------------

export function getOrCreateDeviceId(): string {
  let id = '';
  try { id = localStorage.getItem(DEVICE_KEY) || ''; } catch { /* */ }
  if (!id) {
    // UUID v4 côté client — pas besoin de crypto pour l'identifiant appareil
    id = 'dv-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
    try {
      if ((crypto as any)?.randomUUID) id = 'dv-' + (crypto as any).randomUUID();
    } catch { /* */ }
    try { localStorage.setItem(DEVICE_KEY, id); } catch { /* */ }
  }
  return id;
}

// ---------------------------------------------------------------------------
// Session (localStorage)
// ---------------------------------------------------------------------------

export interface AgentSession {
  token: string;
  expire_le: string; // ISO
}

export interface AgentInfo {
  id: number;
  nom: string;
  prenoms: string;
  nom_complet: string;
  nb_verifications?: number;
}

export function getSession(): AgentSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s: AgentSession = JSON.parse(raw);
    if (!s?.token || !s?.expire_le) return null;
    if (new Date(s.expire_le).getTime() < Date.now()) {
      clearSession();
      return null;
    }
    return s;
  } catch { return null; }
}

export function setSession(s: AgentSession, agent?: AgentInfo): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    if (agent) localStorage.setItem(AGENT_KEY, JSON.stringify(agent));
  } catch { /* */ }
}

export function getAgent(): AgentInfo | null {
  try {
    const raw = localStorage.getItem(AGENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(AGENT_KEY);
  } catch { /* */ }
}

// ---------------------------------------------------------------------------
// HTTP helpers (avec header X-Recouvrement-Token)
// ---------------------------------------------------------------------------

async function jsonFetch<T>(url: string, opts: RequestInit = {}): Promise<T> {
  const session = getSession();
  const headers: any = {
    Accept: 'application/json',
    ...(opts.headers || {}),
  };
  if (opts.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (session?.token) {
    headers['X-Recouvrement-Token'] = session.token;
  }
  const res = await fetch(url, { ...opts, credentials: 'omit', headers });
  const text = await res.text();
  let data: any = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { detail: text }; }
  if (!res.ok) {
    const err: any = new Error(data.detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data.code;
    err.data = data;
    throw err;
  }
  return data as T;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export interface OnboardingPayload {
  nom: string;
  prenoms: string;
  telephone?: string;
  code_acces: string;
}

export async function enregistrer(payload: OnboardingPayload): Promise<{
  token: string; expire_le: string; agent: AgentInfo; nouveau: boolean;
}> {
  const device_id = getOrCreateDeviceId();
  const r = await jsonFetch<any>(`${API_BASE}/enregistrer/`, {
    method: 'POST',
    body: JSON.stringify({ ...payload, device_id }),
  });
  setSession({ token: r.token, expire_le: r.expire_le }, r.agent);
  return r;
}

export async function connexion(code_acces: string): Promise<{
  token: string; expire_le: string; agent: AgentInfo;
}> {
  const device_id = getOrCreateDeviceId();
  const r = await jsonFetch<any>(`${API_BASE}/connexion/`, {
    method: 'POST',
    body: JSON.stringify({ device_id, code_acces }),
  });
  setSession({ token: r.token, expire_le: r.expire_le }, r.agent);
  return r;
}

export interface SessionInfo {
  agent: AgentInfo;
  session: { expire_le: string };
  institut: { campus: string; ville: string };
  annee_academique: string;
}

export async function sessionInfo(): Promise<SessionInfo> {
  return jsonFetch<SessionInfo>(`${API_BASE}/session/`);
}

export async function logVerification(payload: {
  matricule: string;
  methode: 'scan_barcode' | 'scan_qr' | 'recherche_nom' | 'recherche_matricule' | 'cache_local';
  etudiant_nom?: string;
  etudiant_classe?: string;
  etait_a_jour?: boolean | null;
  solde?: string;
  mode_acces?: string;
}): Promise<{ ok: boolean; id: number }> {
  return jsonFetch(`${API_BASE}/log/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function deconnexion(): Promise<void> {
  try { await jsonFetch(`${API_BASE}/deconnexion/`, { method: 'POST' }); } catch { /* */ }
  clearSession();
}
